import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DxGanttComponent,
  DxGanttModule,
  DxSelectBoxModule,
  DxCheckBoxComponent,
  DxPopoverComponent,
  DxDateBoxModule,
  DxTooltipModule,
} from 'devextreme-angular';
import {
  ContextMenuPreparingEvent,
  DependencyDeletedEvent,
  DependencyDeletingEvent,
  DependencyInsertedEvent,
  DependencyInsertingEvent,
  TaskMovingEvent,
  TaskUpdatedEvent,
  TaskUpdatingEvent,
} from 'devextreme/ui/gantt';
import { Subject, takeUntil } from 'rxjs';
import { AppGlobals } from 'src/app/app.globals';
import {
  GanttDependancy,
  GanttDependencyType,
  GanttTask,
} from 'src/app/pages/config/project-config/config-schedule/config-schedule.types';
import { ScheduleService } from 'src/app/services/schedule-item.service';
import { ProjectTaskService } from 'src/app/services/project-task.service';
import { ProjectInfoService } from 'src/app/services/project-info.service';
import { GlobalPopupService } from 'src/app/components/generic-popup/presets/global-popup/global-popup.service';
import { DateFormatter } from 'src/app/helpers/date-formatter';
import { ReasonService } from 'src/app/services/reason.service';
import { Reason, ReasonType } from 'src/app/interfaces/entities/reason';
import { GenericPopupComponent, PopupButton } from 'src/app/components/generic-popup/generic-popup.component';
import { ValueChangedEvent } from 'devextreme/ui/switch';
import { SvgIconComponent } from 'src/app/components/svg-icon/svg-icon.component';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { GpdSegmentedSwitchComponent, SegmentOption } from 'src/app/components/gpd-segmented-switch/gpd-segmented-switch.component';
import { ValidationToastComponent } from 'src/app/components/validation-toast/validation-toast.component';
import { ProjectStatus } from '../../../../../interfaces/entities/project';
import { ActivityLogService } from 'src/app/services/activity-log.service';
import { ActivitySection } from 'src/app/constants/activity-log';
import { LoggingService } from 'src/app/services/logging.service';

import { SearchableDropdownDirective } from '../../../../../directives/searchable-dropdown.directive';
interface ScheduleItem {
  completionDate: Date | null;
  dependency: GanttDependencyType | null;
  dependencyOnId: string | null;
  dependencyOnItemId: number | null;
  dependencyOnItemType: string | null;
  endDate: Date;
  id: string;
  itemId: number;
  itemType: string;
  parentId: string | null;
  startDate: Date;
  title: string;
  progress: number;
  sortingIndex: number;
  isAtRisk: boolean;
}

interface ScheduleChange {
  id: string;
  type: 'date' | 'dependency';
  item: ScheduleItem;
  originalValues: {
    startDate: Date;
    endDate: Date;
    dependency: GanttDependencyType | null;
    dependencyOnId: string | null;
    dependencyOnItemId: number | null;
    dependencyOnItemType: string | null;
  };
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        DxGanttModule,
        GenericPopupComponent,
        DxSelectBoxModule,
        DxCheckBoxComponent,
        SvgIconComponent,
        DxPopoverComponent,
        DxDateBoxModule,
        DxTooltipModule,
        GpdButtonComponent,
        GpdSegmentedSwitchComponent,
        ValidationToastComponent,
        SearchableDropdownDirective,
    ],
    selector: 'app-project-schedule',
    styleUrls: ['./project-schedule.component.css'],
    templateUrl: './project-schedule.component.html'
})
export class ProjectScheduleComponent implements OnInit, AfterViewInit, OnDestroy {
  public ganttTasks: GanttTask[] = [];
  public ganttDependancies: GanttDependancy[] = [];
  public displayedGanttTasks: GanttTask[] = [];
  public displayedGanttDependancies: GanttDependancy[] = [];
  public schedule: ScheduleItem[] = [];
  public taskFillColors = new Map<string, string>();
  public currentDate = new Date();
  public projectId: number;
  public loading = false;
  public packageIdsWithTasks = new Set<number>();
  public saving = false;

  private readonly SCALE_STORAGE_KEY = 'ganttScaleType';
  public currentScaleType: string = localStorage.getItem(this.SCALE_STORAGE_KEY) || 'weeks';

  public showDisableDeps = false;
  public showCircularDepError = false;
  public dontshowAgain = false;

  public statusColors = {
    'not-started': { background: 'rgba(200, 200, 200)', color: '#999999' },
    'done':        { background: 'rgba(202, 220, 189, 0.35)', color: '#629D37' },
    'in-progress': { background: 'rgba(9, 132, 194, 0.35)', color: '#0984C2' },
    'at-risk':     { background: 'rgba(224, 138, 0, 0.35)', color: '#e08a00' },
    'overdue':     { background: 'rgba(243, 58, 58, 0.35)', color: '#ea3f3f' },
  };

  // Track pending updates
  private pendingUpdates = new Map<string, ScheduleChange>();
  // The item the user moved, as opposed to the ones that moved with it; the notification names it.
  private movedItem: ScheduleItem | null = null;
  private saveTimeout: any;
  private expandTimeout: any;
  private ganttResize?: ResizeObserver;
  private ganttResizeTimeout: any;
  private $unsubscribe = new Subject<void>();
  private dateFormatter = new DateFormatter();

  @ViewChild('gantt') public gantt: DxGanttComponent;
  @ViewChild('toast') private toast: ValidationToastComponent;

  // Reason popup state
  public reopenReasons: Reason[] = [];
  public showReasonPopup: boolean = false;
  public selectedReopenReasonId: any = null;

  public safeMode: boolean = true;
  public isAdmin: boolean = AppGlobals.UserPermissions.isAdministrator;
  public projectStatus: string;

  // A getter (not a static array) so the Save button's disabled state reacts to
  // selectedReopenReasonId as the user picks a reason.
  public get reasonPopupButtons(): PopupButton[] {
    return [
      {
        text: $localize`:@@cancel:Cancel`,
        type: 'secondary',
        onClick: () => { this.onCancelReopenPopup(); this.cdr.markForCheck(); },
      },
      {
        text: $localize`:@@save:Save`,
        type: 'primary',
        disabled: this.selectedReopenReasonId === null,
        // GenericPopupComponent closes the popup after any button click unless told
        // not to. A reason is mandatory here, so the popup must stay open when none
        // is selected — closing unconditionally left showReasonPopup stuck `true`
        // (the popup's own visible state was forced closed without it), so it could
        // never reappear for subsequent edits and nothing after that point could be
        // saved. Only onSaveReopenReason() (a valid save) may close it.
        closeOnClick: false,
        onClick: () => {
          if (this.selectedReopenReasonId === null) return;
          this.onSaveReopenReason();
          this.cdr.markForCheck();
        },
      },
    ];
  }

  public disableDepsButtons: PopupButton[] = [
    {
      text: $localize`:@@cancel:Cancel`,
      type: 'secondary',
      onClick: () => { this.showDisableDeps = false; this.safeMode = true; this.repaintGantt(); this.cdr.markForCheck(); },
    },
    {
      text: $localize`:@@disable:Disable`,
      type: 'primary',
      onClick: () => { this.showDisableDeps = false; this.cdr.markForCheck(); },
    },
  ];

  public circularDepButtons: PopupButton[] = [
    {
      text: $localize`:@@ok:OK`,
      type: 'primary',
      onClick: () => { this.showCircularDepError = false; this.cdr.markForCheck(); },
    },
  ];

  private get completedProjectWarning(): string {
    return $localize`:@@editCompletedProjectWarning:The project is complete. To make changes, set the status of the project to "Active".`;
  }

  private get pausedProjectWarning(): string {
    return this.projectStatus === ProjectStatus.INACTIVE
      ? $localize`:@@editInactiveProjectWarning:The project is inactive. To make changes, set the status of the project to "Active".`
      : $localize`:@@editCanceledProjectWarning:The project is canceled. To make changes, set the status of the project to "Active".`;
  }

  private get projectIsLocked(): boolean {
    return this.projectStatus === ProjectStatus.COMPLETED
      || this.projectStatus === ProjectStatus.INACTIVE
      || this.projectStatus === ProjectStatus.CANCELED;
  }

  private get projectLockedWarning(): string {
    return this.projectStatus ===  ProjectStatus.COMPLETED  ? this.completedProjectWarning : this.pausedProjectWarning;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private scheduleService: ScheduleService,
    private route: ActivatedRoute,
    private router: Router,
    private reasonService: ReasonService,
    private projectTaskService: ProjectTaskService,
    private projectInfoService: ProjectInfoService,
    private globalPopupService: GlobalPopupService,
    private activityLog: ActivityLogService,
    private loggingService: LoggingService,
  ) {}

  public async ngOnInit() {
    // Get project ID from route
    this.dontshowAgain = localStorage.getItem('dependancyDisableWarning') === 'true';

    this.route.parent?.params.pipe(takeUntil(this.$unsubscribe)).subscribe(params => {
      this.projectId = +params.id;
      if (this.projectId) {
        this.activityLog.logView({ Section: ActivitySection.PROJECT_SCHEDULE, ProjectId: this.projectId });
        this.loadSchedule();
        this.projectInfoService.getGlobalProjectInfo({ ProjectId: this.projectId }).then(result => {
          if (!result.hasError) {
            this.projectStatus = result.globalProjectInfoResultItem?.status;
            this.cdr.markForCheck();
          }
        });
      }
    });

    // Load reopen reasons
    this.getReopenReasons();
  }

  public ngAfterViewInit() {
    this.watchGanttSize();
  }

  // DevExtreme sizes the Gantt's scrollable area exactly once, as
  // (gantt height − toolbar height), reading both at that instant. Either input can
  // change afterwards and leave the scrollable area too tall, hanging below the Gantt's
  // clipped bottom edge so the last rows can never be scrolled into view:
  //  - the Angular-templated toolbar items render late, growing the toolbar (~2px → 48px),
  //  - the page layout above settles late, shrinking the Gantt host itself (the staging
  //    E2E "gantt is not clipped" failure: sized at 496px, host settles at 449px).
  // Watching only the toolbar missed the second case, so observe both and recompute.
  //
  // updateDimensions() writes only to the Gantt's children (tree list + task view), never
  // to the host or the toolbar, so it cannot retrigger this observer. That holds only
  // while the host stays sized by CSS rather than by content — height="100%" on dx-gantt
  // plus .gantt-container { flex: 1 }. Debounced because each run re-renders the whole
  // task area, which a window-edge drag would otherwise trigger every frame.
  //
  // Must be re-attached after every repaint() — see repaintGantt().
  private watchGanttSize() {
    this.ganttResize?.disconnect();
    const root = this.gantt?.instance.element();
    if (!root) return;
    this.ganttResize = new ResizeObserver(() => {
      clearTimeout(this.ganttResizeTimeout);
      this.ganttResizeTimeout = setTimeout(() => {
        // A hidden Gantt measures 0, from which DevExtreme derives a negative body height
        // (and dereferences a splitter it never built if it was never visible).
        if (!this.gantt?.instance.element()?.offsetHeight) return;
        this.gantt.instance.updateDimensions();
      }, 50);
    });
    this.ganttResize.observe(root);
    const toolbar = root.querySelector('.dx-gantt-toolbar-wrapper');
    if (toolbar) this.ganttResize.observe(toolbar);
  }

  // repaint() empties the Gantt's element and rebuilds it, so the toolbar node the size
  // fix observes is replaced by a new one. Every repaint has to go through here, or the
  // observer is left watching a detached element and the clipping comes back.
  private scrollToToday() {
    const firstTask = this.ganttTasks.sort((a, b) => a.startDate.getTime() - b.startDate.getTime())[0];
    if (firstTask && this.currentDate > firstTask.startDate) {
      setTimeout(() => {
        this.gantt?.instance.scrollToDate(this.currentDate.getTime() - 1000 * 60 * 60 * 24 * 7);
        this.cdr.markForCheck();
      }, 500);
    }
  }

  private repaintGantt() {
    this.gantt?.instance.repaint();
    this.watchGanttSize();
  }

  public ngOnDestroy() {
    this.ganttResize?.disconnect();
    clearTimeout(this.ganttResizeTimeout);
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    if (this.expandTimeout) {
      clearTimeout(this.expandTimeout);
    }
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }

  private async loadPackageIdsWithTasks(): Promise<void> {
    const result = await this.projectTaskService.getProjektAufgaben({ WIProjectItem: { ProjectId: this.projectId } });
    if (result.hasError) return;
    const tasks: any[] = result.projectTasks ?? [];
    this.packageIdsWithTasks = new Set(
      tasks
        .filter(t => t.parentId?.startsWith('TP-'))
        .map(t => parseInt(t.parentId.slice(3), 10))
        .filter(id => !isNaN(id))
    );
  }

  private async loadSchedule() {
    this.loading = true;
    this.cdr.markForCheck();

    this.loadPackageIdsWithTasks();

    try {
      const result = await this.scheduleService.getProjectSchedule(this.projectId);
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        this.schedule = [];
        this.ganttTasks = [];
        this.ganttDependancies = [];
        return;
      }

      if (result && result.schedule && result.schedule.length > 0) {
        // Map API response to internal schedule format
        this.schedule = result.schedule.map(item => ({
          completionDate: item.completionDate ? this.dateFormatter.fromDateOnly(item.completionDate) : null,
          dependency: item.dependency,
          dependencyOnId: item.dependencyOnId,
          dependencyOnItemId: item.dependencyOnItemId,
          dependencyOnItemType: item.dependencyOnItemType,
          startDate: this.dateFormatter.fromDateOnly(item.startDate),
          endDate: this.dateFormatter.fromDateOnly(item.endDate),
          id: item.id,
          itemId: item.itemId,
          itemType: item.itemType,
          parentId: item.parentId,
          title: item.title,
          progress: item.progress,
          sortingIndex: item.sortingIndex,
          isAtRisk: item.isAtRisk,
        }));
        this.calculateDatesAndMapTasks();
      } else {
        // If no schedule data, show empty state
        this.schedule = [];
        this.ganttTasks = [];
        this.ganttDependancies = [];
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage('Error loading schedule data: ' + error);
      this.schedule = [];
      this.ganttTasks = [];
      this.ganttDependancies = [];
    }

    this.scrollToToday();

    this.loading = false;
    this.cdr.markForCheck();
  }

  public onContextMenuPreparing(e: ContextMenuPreparingEvent) {
    if (e.targetType === 'task') {
      e.cancel = true;
    }
  }

  // Overdue is checked before at-risk so it always takes precedence: an item that is
  // overdue (or has an overdue descendant) never displays as merely at-risk, even if it
  // also satisfies the at-risk pace formula (which it almost always will once overdue).
  public getItemStatus(item: ScheduleItem, childHasOverdue = false, childHasAtRisk = false): keyof typeof this.statusColors {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (item.completionDate != null) return 'done';
    if (childHasOverdue || item.endDate < today) return 'overdue';
    if (item.isAtRisk || childHasAtRisk) return 'at-risk';
    if (item.startDate < today) return 'in-progress';
    return 'not-started';
  }

  private getItemsWithOverdueDescendants(): Set<string> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const childrenMap = new Map<string, ScheduleItem[]>();
    for (const item of this.schedule) {
      if (item.parentId) {
        if (!childrenMap.has(item.parentId)) childrenMap.set(item.parentId, []);
        childrenMap.get(item.parentId).push(item);
      }
    }

    const overdueAncestors = new Set<string>();

    const hasOverdueDescendant = (itemId: string): boolean => {
      const children = childrenMap.get(itemId) ?? [];
      for (const child of children) {
        if (child.completionDate == null && child.endDate < today) {
          overdueAncestors.add(itemId);
          return true;
        }
        if (hasOverdueDescendant(child.id)) {
          overdueAncestors.add(itemId);
          return true;
        }
      }
      return false;
    };

    for (const item of this.schedule) {
      hasOverdueDescendant(item.id);
    }

    return overdueAncestors;
  }

  private getItemsWithAtRiskDescendants(): Set<string> {
    const childrenMap = new Map<string, ScheduleItem[]>();
    for (const item of this.schedule) {
      if (item.parentId) {
        if (!childrenMap.has(item.parentId)) childrenMap.set(item.parentId, []);
        childrenMap.get(item.parentId).push(item);
      }
    }

    const atRiskAncestors = new Set<string>();

    const hasAtRiskDescendant = (itemId: string): boolean => {
      const children = childrenMap.get(itemId) ?? [];
      for (const child of children) {
        if (child.completionDate == null && child.isAtRisk) {
          atRiskAncestors.add(itemId);
          return true;
        }
        if (hasAtRiskDescendant(child.id)) {
          atRiskAncestors.add(itemId);
          return true;
        }
      }
      return false;
    };

    for (const item of this.schedule) {
      hasAtRiskDescendant(item.id);
    }

    return atRiskAncestors;
  }

  public calculateDatesAndMapTasks() {
    this.ganttTasks = [];
    this.ganttDependancies = [];

    const overdueParents = this.getItemsWithOverdueDescendants();
    const atRiskParents = this.getItemsWithAtRiskDescendants();

    // Map schedule items to gantt tasks
    this.taskFillColors = new Map<string, string>();
    this.ganttTasks = this.schedule.map(item => {
      const status = this.getItemStatus(item, overdueParents.has(item.id), atRiskParents.has(item.id));
      this.taskFillColors.set(item.id, this.statusColors[status].color);
      return {
        color: this.statusColors[status].background,
        endDate: item.completionDate ?? item.endDate,
        id: item.id, 
        parentId: item.parentId,
        startDate: item.startDate,
        title: item.title,
        progress: item.progress,
        sortingIndex: item.sortingIndex,
        itemId: item.itemId,
        itemType: item.itemType,
      };
    });

    // Build dependencies array
    this.schedule.forEach(item => {
      if (item.dependencyOnId && item.dependency != null && this.ganttTasks.find(t => t.id == item.dependencyOnId)) {
        this.ganttDependancies.push({
          id: this.ganttDependancies.length,
          dependancyOnId: item.dependencyOnId,
          dependencyType: item.dependency,
          taskId: item.id,
        });
      }
    });

    this.applyLevelFilter();
    this.cdr.markForCheck();
  }

  private applyLevelFilter(): void {
    this.displayedGanttTasks = this.ganttTasks;
    this.displayedGanttDependancies = this.ganttDependancies;
  }

  private getTaskIdsByLevel(level: number): string[] {
    const levelMap: Record<number, string> = { 0: 'Phase', 1: 'Milestone', 2: 'Package' };
    const itemType = levelMap[level];
    return this.ganttTasks
      .filter(t => t.itemType === itemType)
      .map(t => t.id);
  }

  // Handle task updating event (before update)
  public onTaskUpdating(e: TaskUpdatingEvent) {
    if (this.projectIsLocked) {
      e.cancel = true;
      this.globalPopupService.showWarningPopup(this.projectLockedWarning);
      return;
    }
    const item = this.schedule.find(s => s.id === e.key);
    if (item?.completionDate != null) {
      e.cancel = true;
      this.toast.showToast(this.completedPackageTooltip);
      return;
    }
    if (item?.itemType === 'Package' && (e.values?.progress == 100 || this.isChildDone(e.key))) {
      e.cancel = true;
      if (this.packageIdsWithTasks.has(item.itemId)) {
        this.toast.showToast(this.completedPackageTooltip);
      }
      return;
    }

    this.movedItem ??= item ?? null;

    let newStart = e.newValues.startDate ?? e.values.startDate;
    let newEnd = e.newValues.endDate ?? e.values.endDate;

    // Round to whole days
    newStart = new Date(Math.round(newStart.getTime() / 1000 / 60 / 60 / 24) * 1000 * 60 * 60 * 24);
    newEnd = new Date(Math.round(newEnd.getTime() / 1000 / 60 / 60 / 24) * 1000 * 60 * 60 * 24);

    if (item?.itemType === 'Phase' || item?.itemType === 'Milestone') {
      // DevExtreme's native drag rigidly moves the whole subtree, including closed
      // items. Cancel it and drive the shift ourselves so closed descendants stay put
      // and the parent's own bounds are recalculated to include them (see
      // applyParentDateShift / recalculateBoundsUpward).
      e.cancel = true;
      const deltaMs = e.newValues.startDate
        ? newStart.getTime() - item.startDate.getTime()
        : newEnd.getTime() - item.endDate.getTime();
      this.applyParentDateShift(item, deltaMs);
      return;
    }

    // Ensure minimum duration of 1 day
    if (e.newValues.startDate) {
      const dur = (newEnd.getTime() - newStart.getTime()) / 1000 / 60 / 60 / 24;
      if (dur < 1) {
        e.newValues.startDate = new Date(newEnd.getTime() - 24 * 60 * 60 * 1000);
      }
    }
    if (e.newValues.endDate) {
      const dur = (newEnd.getTime() - newStart.getTime()) / 1000 / 60 / 60 / 24;
      if (dur < 1) {
        e.newValues.endDate = new Date(newStart.getTime() + 24 * 60 * 60 * 1000);
      }
    }
  }

  onTaskMoving(e: TaskMovingEvent) {
    if (this.projectIsLocked) {
      e.cancel = true;
      this.globalPopupService.showWarningPopup(this.projectLockedWarning);
      return;
    }
    const item = this.schedule.find(s => s.id === e.key);
    if (item?.completionDate != null) {
      e.cancel = true;
      this.toast.showToast(this.completedPackageTooltip);
      return;
    }
    if (item?.itemType === 'Package' && (item.progress === 100 || this.isChildDone(e.key))) {
      e.cancel = true;
      if (this.packageIdsWithTasks.has(item.itemId)) {
        this.toast.showToast(this.completedPackageTooltip);
      }
    }
  }

  isChildDone(taskId: string): boolean {
    const tasks = this.schedule.filter(s => s.parentId === taskId);
    for (const task of tasks) {
      if (task.completionDate != null || task.progress == 100) {
        return true;
      }
      if (this.isChildDone(task.id)) {
        return true;
      }
    }

    return false;
  }

  // Handle task updates from Gantt chart
  public onTaskUpdated(e: TaskUpdatedEvent) {
    const scheduleItem = this.schedule.find(s => s.id === e.key);

    let start = e.values.startDate ?? scheduleItem.startDate;
    let end = e.values.endDate ?? scheduleItem.endDate;
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    if (scheduleItem && (scheduleItem.startDate !== start || scheduleItem.endDate !== end)) {
      // Store original values before updating (only if not already tracked)
      const existingChange = this.pendingUpdates.get(scheduleItem.id);
      const originalValues = existingChange?.originalValues ?? {
        startDate: new Date(scheduleItem.startDate),
        endDate: new Date(scheduleItem.endDate),
        dependency: scheduleItem.dependency,
        dependencyOnId: scheduleItem.dependencyOnId,
        dependencyOnItemId: scheduleItem.dependencyOnItemId,
        dependencyOnItemType: scheduleItem.dependencyOnItemType,
      };

      scheduleItem.startDate = start;
      scheduleItem.endDate = end;

      // Add to pending updates and trigger debounced save
      this.pendingUpdates.set(scheduleItem.id, {
        id: scheduleItem.id,
        type: 'date',
        item: scheduleItem,
        originalValues,
      });
      this.recalculateBoundsUpward(scheduleItem.id);
      this.debouncedSave();
    }
  }

  onDependencyInserting(e: DependencyInsertingEvent) {
    if (this.projectIsLocked) {
      e.cancel = true;
      this.globalPopupService.showWarningPopup(this.projectLockedWarning);
      return;
    }
    const succ = this.schedule.find(s => s.id == e.values.taskId);

    if (succ.itemType != 'Package' || succ.progress == 100) {
      e.cancel = true;
      return;
    }

    // Cancel if successor already has a dependency
    if (succ.dependencyOnId) {
      e.cancel = true;
      return;
    }

    // Check for circular dependency
    if (this.wouldCreateCircularDependency(e.values.taskId, e.values.dependancyOnId)) {
      this.showCircularDepError = true;
      this.cdr.markForCheck();
      e.cancel = true;
    }
  }

  /**
   * Checks if adding a dependency from predecessorId -> successorId would create a circular dependency.
   * This traverses the dependency chain starting from predecessorId to see if it eventually leads back to successorId.
   * Also checks children of the predecessor since parent dates are determined by their children.
   */
  private wouldCreateCircularDependency(successorId: string, predecessorId: string): boolean {
    const visited = new Set<string>();
    const queue: string[] = [predecessorId];

    // Also add all children of the predecessor (recursively) since parent dates are determined by children
    const addChildrenToQueue = (parentId: string) => {
      const children = this.schedule.filter(s => s.parentId === parentId);
      for (const child of children) {
        if (!visited.has(child.id)) {
          queue.push(child.id);
          addChildrenToQueue(child.id);
        }
      }
    };
    addChildrenToQueue(predecessorId);

    while (queue.length > 0) {
      const currentId = queue.shift();

      if (currentId === successorId) {
        return true;
      }

      if (visited.has(currentId)) {
        continue;
      }
      visited.add(currentId);

      // Find the task that currentId depends on
      const currentTask = this.schedule.find(s => s.id === currentId);
      if (currentTask?.dependencyOnId) {
        queue.push(currentTask.dependencyOnId);
        // Also add children of the dependency target
        addChildrenToQueue(currentTask.dependencyOnId);
      }
    }

    return false;
  }

  // Handle dependency insertion
  public onDependencyInserted(e: DependencyInsertedEvent) {
    const task = this.schedule.find(s => s.id === e.values.taskId);
    const depTask = this.schedule.find(s => s.id === e.values.dependancyOnId);

    if (task.completionDate) {
      return;
    }

    if (task && depTask) {
      // Store original values before updating (only if not already tracked)
      const existingChange = this.pendingUpdates.get(task.id);
      const originalValues = existingChange?.originalValues ?? {
        startDate: new Date(task.startDate),
        endDate: new Date(task.endDate),
        dependency: task.dependency,
        dependencyOnId: task.dependencyOnId,
        dependencyOnItemId: task.dependencyOnItemId,
        dependencyOnItemType: task.dependencyOnItemType,
      };

      task.dependencyOnId = e.values.dependancyOnId;
      task.dependency = e.values.dependencyType;
      task.dependencyOnItemId = depTask.itemId;
      task.dependencyOnItemType = depTask.itemType;

      // Add to pending updates and trigger debounced save
      this.pendingUpdates.set(task.id, { id: task.id, type: 'dependency', item: task, originalValues });
      this.debouncedSave();
    }
  }

  public onDependencyDeleting(e: DependencyDeletingEvent) {
    if (this.projectIsLocked) {
      e.cancel = true;
      this.globalPopupService.showWarningPopup(this.projectLockedWarning);
    }
  }

  // Handle dependency deletion
  public onDependencyDeleted(e: DependencyDeletedEvent) {
    const task = this.schedule.find(s => s.id === e.values.taskId);

    if (task.completionDate) {
      return;
    }

    if (task) {
      // Store original values before updating (only if not already tracked)
      const existingChange = this.pendingUpdates.get(task.id);
      const originalValues = existingChange?.originalValues ?? {
        startDate: new Date(task.startDate),
        endDate: new Date(task.endDate),
        dependency: task.dependency,
        dependencyOnId: task.dependencyOnId,
        dependencyOnItemId: task.dependencyOnItemId,
        dependencyOnItemType: task.dependencyOnItemType,
      };

      task.dependencyOnId = null;
      task.dependency = null;
      task.dependencyOnItemId = null;
      task.dependencyOnItemType = null;

      // Add to pending updates and trigger debounced save
      this.pendingUpdates.set(task.id, { id: task.id, type: 'dependency', item: task, originalValues });
      this.debouncedSave();
    }
  }
  // Debounce save: wait 1 second after last update before saving
  private debouncedSave() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    this.saveTimeout = setTimeout(() => {
      let isDateChange = false;

      this.pendingUpdates.forEach(change => {
        if (change.type === 'date') {
          isDateChange = true;
        }
      });
      // Show reason popup
      if (isDateChange) {
        this.showReasonPopup = true;
      } else {
        this.savePendingUpdates();
      }
      this.cdr.markForCheck();
    }, 200);
  }

  // Save all pending updates in a single batch request
  private async savePendingUpdates() {
    if (this.pendingUpdates.size === 0) {
      return;
    }

    this.saving = true;
    this.cdr.markForCheck();

    try {
      const scheduleItems = Array.from(this.pendingUpdates.values()).map(item => ({
        itemId: item.item.itemId,
        itemType: item.item.itemType,
        startDate: this.dateFormatter.toDateOnly(item.item.startDate),
        endDate: this.dateFormatter.toDateOnly(item.item.endDate),
        dependency: item.item.dependency,
        dependencyOnItemId: item.item.dependencyOnItemId,
        dependencyOnItemType: item.item.dependencyOnItemType,
      }));

      const result = await this.scheduleService.writeProjectScheduleItems({
        projectId: this.projectId,
        scheduleItems: scheduleItems,
        reopenId: this.selectedReopenReasonId,
        movedItemId: this.movedItem?.itemId ?? null,
        movedItemType: this.movedItem?.itemType ?? null,
      });

      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
      } else {
        this.pendingUpdates.clear();
        this.selectedReopenReasonId = null;
        this.movedItem = null;
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage('Error saving schedule updates: ' + error);
    }

    this.saving = false;
    this.cdr.markForCheck();
  }

  public onCellDateChanged(taskId: string, field: 'startDate' | 'endDate', newValue: Date) {
    if (this.projectIsLocked) {
      this.globalPopupService.showWarningPopup(this.projectLockedWarning);
      return;
    }
    if (!newValue) return;
    const scheduleItem = this.schedule.find(s => s.id === taskId);
    if (!scheduleItem) return;
    if (scheduleItem.completionDate != null) return;
    if (scheduleItem.itemType === 'Package' && (scheduleItem.progress === 100 || this.isChildDone(taskId))) return;

    this.movedItem ??= scheduleItem;

    const newDate = new Date(newValue);
    newDate.setHours(0, 0, 0, 0);

    if (scheduleItem.itemType === 'Phase' || scheduleItem.itemType === 'Milestone') {
      const oldDate = field === 'startDate' ? new Date(scheduleItem.startDate) : new Date(scheduleItem.endDate);
      const deltaMs = newDate.getTime() - oldDate.getTime();
      this.applyParentDateShift(scheduleItem, deltaMs);
      return;
    }

    let newStart = field === 'startDate' ? newDate : new Date(scheduleItem.startDate);
    let newEnd = field === 'endDate' ? newDate : new Date(scheduleItem.endDate);

    if (newEnd <= newStart) {
      if (field === 'startDate') newEnd = new Date(newStart.getTime() + 24 * 60 * 60 * 1000);
      else newStart = new Date(newEnd.getTime() - 24 * 60 * 60 * 1000);
    }

    const existingChange = this.pendingUpdates.get(scheduleItem.id);
    const originalValues = existingChange?.originalValues ?? {
      startDate: new Date(scheduleItem.startDate),
      endDate: new Date(scheduleItem.endDate),
      dependency: scheduleItem.dependency,
      dependencyOnId: scheduleItem.dependencyOnId,
      dependencyOnItemId: scheduleItem.dependencyOnItemId,
      dependencyOnItemType: scheduleItem.dependencyOnItemType,
    };

    const startDelta = newStart.getTime() - scheduleItem.startDate.getTime();
    const endDelta = newEnd.getTime() - scheduleItem.endDate.getTime();

    scheduleItem.startDate = newStart;
    scheduleItem.endDate = newEnd;

    this.pendingUpdates.set(scheduleItem.id, {
      id: scheduleItem.id,
      type: 'date',
      item: scheduleItem,
      originalValues,
    });

    // Shift dependent packages, matching the Gantt's drag-time cascade.
    this.cascadeDependencyShift(scheduleItem.id, startDelta, endDelta, new Set<string>([scheduleItem.id]));

    this.recalculateBoundsUpward(scheduleItem.id);

    this.calculateDatesAndMapTasks();
    this.debouncedSave();
  }

  private addToPendingUpdates(item: ScheduleItem): void {
    const existing = this.pendingUpdates.get(item.id);
    if (!existing) {
      this.pendingUpdates.set(item.id, {
        id: item.id,
        type: 'date',
        item,
        originalValues: {
          startDate: new Date(item.startDate),
          endDate: new Date(item.endDate),
          dependency: item.dependency,
          dependencyOnId: item.dependencyOnId,
          dependencyOnItemId: item.dependencyOnItemId,
          dependencyOnItemType: item.dependencyOnItemType,
        },
      });
    }
  }

  private shiftChildren(parentId: string, deltaMs: number, movedIds?: Set<string>): void {
    const children = this.schedule.filter(s => s.parentId === parentId);
    for (const child of children) {
      // Closed items are frozen in place: a completed Package/Milestone must not
      // move when its parent is shifted, and (per business invariant) its own
      // children are closed too, so there's nothing further to recurse into.
      if (child.completionDate != null) continue;
      this.addToPendingUpdates(child);
      child.startDate = new Date(child.startDate.getTime() + deltaMs);
      child.endDate = new Date(child.endDate.getTime() + deltaMs);
      movedIds?.add(child.id);
      this.shiftChildren(child.id, deltaMs, movedIds);
    }
  }

  /**
   * Shifts a Phase/Milestone by `deltaMs`, applied to itself and to open
   * (non-completed) descendants only — closed descendants are left in place.
   * The item's own bounds (and its ancestors') are then recalculated from
   * their children so they still bound any closed items that didn't move
   * (see recalculateBoundsUpward).
   */
  private applyParentDateShift(scheduleItem: ScheduleItem, deltaMs: number): void {
    if (deltaMs === 0) return;
    const hadClosedDescendant = this.isChildDone(scheduleItem.id);
    const movedIds = new Set<string>([scheduleItem.id]);
    this.addToPendingUpdates(scheduleItem);
    scheduleItem.startDate = new Date(scheduleItem.startDate.getTime() + deltaMs);
    scheduleItem.endDate = new Date(scheduleItem.endDate.getTime() + deltaMs);
    this.shiftChildren(scheduleItem.id, deltaMs, movedIds);
    // Cascade to packages that depend on the moved subtree (but not internal dependents, which already moved).
    for (const movedId of Array.from(movedIds)) {
      this.cascadeDependencyShift(movedId, deltaMs, deltaMs, movedIds);
    }
    // A shift can leave a node partway down the subtree (e.g. a Milestone under
    // a shifted Phase) with a mix of shifted-open and frozen-closed children, so
    // bounds must be re-derived bottom-up through the whole subtree, not just at
    // the top. Ancestors above the shifted item are then fixed on the way up.
    this.recalculateBoundsDownward(scheduleItem.id);
    this.recalculateBoundsUpward(scheduleItem.id);
    this.calculateDatesAndMapTasks();
    this.debouncedSave();
    if (hadClosedDescendant) {
      this.toast.showToast(this.completedChildTooltip);
    }
  }

  /**
   * Recomputes `itemId`'s own start/end from its direct children (min start,
   * max end), if it has any. No-op for leaf items (Packages). Returns whether
   * the bounds actually changed.
   */
  private recalculateBounds(itemId: string): boolean {
    const item = this.schedule.find(s => s.id === itemId);
    if (!item) return false;
    const children = this.schedule.filter(s => s.parentId === itemId);
    if (children.length === 0) return false;

    const minStart = children.reduce((min, c) => (c.startDate < min ? c.startDate : min), children[0].startDate);
    const maxEnd = children.reduce((max, c) => (c.endDate > max ? c.endDate : max), children[0].endDate);

    if (item.startDate.getTime() === minStart.getTime() && item.endDate.getTime() === maxEnd.getTime()) {
      return false;
    }

    this.addToPendingUpdates(item);
    item.startDate = minStart;
    item.endDate = maxEnd;
    return true;
  }

  /**
   * Post-order recalculation of `itemId` and every descendant's bounds, deepest
   * first. Needed because a rigid shift can leave a node partway down the
   * subtree (e.g. a Milestone under a shifted Phase) with a mix of
   * shifted-open and frozen-closed children whose combined bounds differ from
   * what the rigid shift assigned to that node directly.
   */
  private recalculateBoundsDownward(itemId: string): void {
    const children = this.schedule.filter(s => s.parentId === itemId);
    for (const child of children) {
      this.recalculateBoundsDownward(child.id);
    }
    this.recalculateBounds(itemId);
  }

  /**
   * Walks from `itemId`'s parent up through its ancestors (Package -> Milestone
   * -> Phase), recalculating each one's bounds from its own children in turn.
   * This is what implements "the milestone/phase bounds expand to include a
   * closed item that didn't move" after a shift that skipped closed
   * descendants.
   */
  private recalculateBoundsUpward(itemId: string): void {
    const item = this.schedule.find(s => s.id === itemId);
    let current = item?.parentId ? this.schedule.find(s => s.id === item.parentId) : undefined;
    while (current) {
      this.recalculateBounds(current.id);
      current = current.parentId ? this.schedule.find(s => s.id === current.parentId) : undefined;
    }
  }

  /**
   * Shifts packages that depend on `predecessorId` so their dependency
   * constraint is preserved, mirroring what the DevExtreme Gantt does on drag
   * when Safe Mode (validateDependencies) is enabled. Without this, changing a
   * date via the date picker bypassed the Gantt's own cascade and left
   * dependents in place. Dependents are shifted rigidly (duration and gap kept)
   * and their own dependents are shifted in turn.
   *
   * @param startDelta change applied to the predecessor's start, in ms
   * @param endDelta change applied to the predecessor's end, in ms
   * @param movedIds ids already shifted in this operation; prevents an item that
   *   moved as part of a parent subtree from being shifted twice.
   */
  private cascadeDependencyShift(
    predecessorId: string,
    startDelta: number,
    endDelta: number,
    movedIds: Set<string>,
  ): void {
    if (!this.safeMode) return;
    if (startDelta === 0 && endDelta === 0) return;

    const successors = this.schedule.filter(s => s.dependencyOnId === predecessorId && s.dependency != null);

    for (const succ of successors) {
      if (movedIds.has(succ.id) || succ.completionDate != null) continue;

      // FS/FF are anchored to the predecessor's finish, SS/SF to its start.
      const shift =
        succ.dependency === GanttDependencyType.SS || succ.dependency === GanttDependencyType.SF
          ? startDelta
          : endDelta;
      if (shift === 0) continue;

      this.addToPendingUpdates(succ);
      succ.startDate = new Date(succ.startDate.getTime() + shift);
      succ.endDate = new Date(succ.endDate.getTime() + shift);
      movedIds.add(succ.id);

      // The package moved rigidly, so its children and its own dependents shift by the same amount.
      this.shiftChildren(succ.id, shift, movedIds);
      this.cascadeDependencyShift(succ.id, shift, shift, movedIds);
      // succ may live under a different Milestone/Phase than the predecessor; make
      // sure that branch's bounds are recalculated too.
      this.recalculateBoundsUpward(succ.id);
    }
  }

  public isTaskEditable(taskId: string): boolean {
    const item = this.schedule.find(s => s.id === taskId);
    if (!item || item.completionDate != null) return false;
    if (item.itemType === 'Milestone' || item.itemType === 'Phase') return true;
    return item.itemType === 'Package' && item.progress !== 100 && !this.isChildDone(taskId);
  }

  public expandedLevel: number = 2;
  public readonly completedPackageTooltip = $localize`:@@completedPackageNoEdit:Completed packages cannot be edited. Open a task in the package to make it editable again.`;
  public readonly completedChildTooltip = $localize`:@@completedChildNoMove:Completed items were not moved. The bounds were adjusted to include them.`;

  public readonly expandOptions: SegmentOption[] = [
    { label: $localize`:@@ganttLevelPhases:Phasen`, value: 0 },
    { label: $localize`:@@ganttLevelMilestones:Meilensteine`, value: 1 },
    { label: $localize`:@@ganttLevelPackages:Arbeitspakete`, value: 2 },
  ];

  public setExpandLevel(level: number): void {
    this.expandedLevel = level;
    this.cdr.markForCheck();

    // Cancel any pending collapse/expand chain so rapid level switching is
    // deterministic (only the latest selection runs to completion). Without
    // this, overlapping timeout chains interleave and leave a partially
    // expanded state.
    if (this.expandTimeout) {
      clearTimeout(this.expandTimeout);
    }

    this.expandTimeout = setTimeout(() => {
      // DevExtreme v25.1 reliably syncs the left tree list and the right chart
      // pane for exactly two operations: collapseAll() (full collapse) and
      // repaint() (re-render to the default, fully-expanded state). Building a
      // full expansion out of expandTask() bursts instead leaves the right chart
      // pane too short, so rows below its stale height render blank. So:

      // Full depth (Arbeitspakete): repaint() re-renders both panes fully
      // expanded and correctly sized - the same state as the initial page load.
      if (level >= this.expandOptions.length - 1) {
        this.repaintGantt();
        this.expandTimeout = null;
        this.cdr.markForCheck();
        return;
      }

      // Otherwise start from a fully collapsed, in-sync baseline.
      this.gantt?.instance.collapseAll();

      // Zero depth (Phasen): collapsed is the target, nothing more to do.
      if (level <= 0) {
        this.expandTimeout = null;
        this.cdr.markForCheck();
        return;
      }

      // Intermediate depth (Meilensteine): expand the levels above the target in
      // a SEPARATE tick. collapseAll() + expandTask() in the same tick leaves the
      // chart pane stale (the expands cancel the collapse's row removal on the
      // chart side); letting the collapse render first keeps both panes in sync.
      this.expandTimeout = setTimeout(() => {
        for (let parentLevel = 0; parentLevel < level; parentLevel++) {
          for (const id of this.getTaskIdsByLevel(parentLevel)) {
            this.gantt?.instance.expandTask(id);
          }
        }
        this.expandTimeout = null;
        this.cdr.markForCheck();
      });
    }, 50);
  }


  public navigateToTasks(itemId: number): void {
    this.router.navigate(['../../tasks'], {
      relativeTo: this.route,
      queryParams: { packageId: itemId },
    });
  }

  public getTasksUrl(itemId: number): string {
    const tree = this.router.createUrlTree(['../../tasks'], {
      relativeTo: this.route,
      queryParams: { packageId: itemId },
    });
    return this.router.serializeUrl(tree);
  }

  public onTaskLinkClick(event: MouseEvent, itemId: number): void {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    this.navigateToTasks(itemId);
  }

  public onOptionChanged(e: any): void {
    if (e.name === 'scaleType') {
      localStorage.setItem(this.SCALE_STORAGE_KEY, e.value);
      this.currentScaleType = e.value;
    }
  }

  public exportingPdf = false;

  // exportGantt's own dateRange option draws the bars right but labels the header from the
  // gantt's padded scale start (years off), so fit the chart itself to the task span, export
  // everything, then restore.
  public async exportPdf(): Promise<void> {
    const gantt = this.gantt?.instance;
    if (!gantt || this.ganttTasks.length === 0 || this.exportingPdf) return;
    this.exportingPdf = true;
    this.cdr.markForCheck();

    const start = new Date(Math.min(...this.ganttTasks.map(t => +t.startDate)));
    const end = new Date(Math.max(...this.ganttTasks.map(t => +t.endDate)));
    const previous = { startDateRange: gantt.option('startDateRange'), endDateRange: gantt.option('endDateRange') };
    try {
      // Loaded on click: this route is eager, and jsPDF would otherwise sit in the initial bundle.
      const [{ jsPDF }, { applyPlugin }, { exportGantt }] = await Promise.all([
        import('jspdf'),
        import('jspdf-autotable'),
        import('devextreme/common/export/pdf'),
      ]);
      // The gantt exporter calls doc.autoTable(); jspdf-autotable 5 only adds it on request.
      applyPlugin(jsPDF);
      gantt.option({
        startDateRange: new Date(start.getFullYear(), start.getMonth(), 1),
        endDateRange: new Date(end.getFullYear(), end.getMonth() + 1, 0),
      });
      // ponytail: fixed wait for the gantt to re-render the new range; 300ms was enough in the prototype
      await new Promise(resolve => setTimeout(resolve, 300));
      const doc = await exportGantt({
        component: gantt,
        createDocumentMethod: (args: any) => new jsPDF(args),
        format: 'auto',
        exportMode: 'all',
        dateRange: 'all',
      });
      const fileName = `schedule_${this.projectId}.pdf`;
      doc.save(fileName);
      this.loggingService.logFileExport(doc.output('blob'), fileName, {
        section: ActivitySection.PROJECT_SCHEDULE,
        projectId: this.projectId,
      });
    } catch (error) {
      console.error('Schedule PDF export failed', error);
      AppGlobals.ShowErrorMessage($localize`:@@scheduleExportFailed:The schedule could not be exported. Please try again.`);
    } finally {
      gantt.option(previous);
      // The live view turns an undefined range into new Date(undefined), so the bars vanish;
      // a repaint rebuilds the view from the options and gets the auto range back.
      this.repaintGantt();
      // The repaint re-expands everything and resets the scroll; put both back.
      if (this.expandedLevel < this.expandOptions.length - 1) this.setExpandLevel(this.expandedLevel);
      this.scrollToToday();
      this.exportingPdf = false;
      this.cdr.markForCheck();
    }
  }

  public getScheduleProgress(taskId: string): number {
    return this.schedule.find(s => s.id === taskId)?.progress ?? 0;
  }

  public getDuration(startDate: Date, endDate: Date) {
    const diff = endDate.getTime() - startDate.getTime();
    const diffDays = Math.floor(diff / (24 * 60 * 60 * 1000));
    return diffDays;
  }

  // Get reopen reasons from the API
  public async getReopenReasons() {
    try {
      const result = await this.reasonService.getReasons({ type: ReasonType.ScheduleChange });
      if (result.hasError === true) {
        AppGlobals.ShowErrorMessage(result.feedback);
      } else {
        this.reopenReasons = [
          ...result.reasons,
          { id: 0, name: $localize`:@@other:Other`, type: ReasonType.ScheduleChange, isDeleted: false },
        ];
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  // Save the reopen reason and proceed with the save
  public onSaveReopenReason() {
    this.showReasonPopup = false;
    this.savePendingUpdates();
  }

  // Cancel the reopen popup and revert changes
  public onCancelReopenPopup() {
    this.showReasonPopup = false;
    this.selectedReopenReasonId = null;

    // Revert all pending changes to their original values
    this.pendingUpdates.forEach(change => {
      const scheduleItem = this.schedule.find(s => s.id === change.id);
      if (scheduleItem) {
        scheduleItem.startDate = change.originalValues.startDate;
        scheduleItem.endDate = change.originalValues.endDate;
        scheduleItem.dependency = change.originalValues.dependency;
        scheduleItem.dependencyOnId = change.originalValues.dependencyOnId;
        scheduleItem.dependencyOnItemId = change.originalValues.dependencyOnItemId;
        scheduleItem.dependencyOnItemType = change.originalValues.dependencyOnItemType;
      }
    });

    this.pendingUpdates.clear();
    this.movedItem = null;
    this.calculateDatesAndMapTasks();
  }

  onSafeModeChanged(e: ValueChangedEvent) {
    if (e.value === false && e.previousValue === true && !this.dontshowAgain) {
      this.showDisableDeps = true;
    }
    this.repaintGantt();
  }
  onDontShowAgainChanged(e: ValueChangedEvent) {
    if (e.value === true) {
      this.dontshowAgain = true;
      localStorage.setItem('dependancyDisableWarning', 'true');
    } else {
      this.dontshowAgain = false;
      localStorage.removeItem('dependancyDisableWarning');
    }
  }
}
